import { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Shield, AlertTriangle, Database } from 'lucide-react';
import { fetchHealth } from '../lib/api';

interface FleetStatus {
  active: number;
  quarantined: number;
  halted: number;
  auditPersistent: boolean;
}

export default function FleetStatusBar() {
  const [status, setStatus] = useState<FleetStatus>({
    active: 0,
    quarantined: 0,
    halted: 0,
    auditPersistent: false,
  });
  const [loading, setLoading] = useState(true);

  const loadStatus = async () => {
    const data = await fetchHealth();
    if (data) {
      const capsules = data.capsules || {};
      const total = capsules.total || 0;
      const healthy = capsules.healthy || 0;
      const unhealthy = capsules.unhealthy || 0;
      const halted = total - healthy - unhealthy;

      setStatus({
        active: healthy,
        quarantined: unhealthy,
        halted: halted > 0 ? halted : 0,
        auditPersistent: data.auditTrail?.persistent ?? false,
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    loadStatus();
    const interval = setInterval(loadStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Card className="bg-gray-900 border-[#3A352F]">
      <CardContent className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-4">
          {/* Active agents */}
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-ag-active" />
            <Badge
              variant="outline"
              className="text-ag-active border-ag-active text-xs"
            >
              Active: {loading ? '--' : status.active}
            </Badge>
          </div>

          {/* Quarantined agents. text-ag-quarantine (#A78BFA, violet), not
              text-amber-400 (#FBBF24) -- amber is the warning/flagged colour;
              a quarantined agent is contained, not flagged. See
              ui/config/governanceColors.ts for the semantic rationale. */}
          <div className="flex items-center gap-2">
            <AlertTriangle
              className={`w-4 h-4 ${
                status.quarantined > 0 ? 'text-ag-quarantine' : 'text-gray-500'
              }`}
            />
            <Badge
              variant="outline"
              className={`text-xs ${
                status.quarantined > 0
                  ? 'text-ag-quarantine border-ag-quarantine'
                  : 'text-gray-500 border-[#4A443D]'
              }`}
            >
              Quarantined: {loading ? '--' : status.quarantined}
            </Badge>
          </div>

          {/* Halted agents */}
          <div className="flex items-center gap-2">
            <Shield
              className={`w-4 h-4 ${
                status.halted > 0 ? 'text-ag-critical' : 'text-gray-500'
              }`}
            />
            <Badge
              variant="outline"
              className={`text-xs ${
                status.halted > 0
                  ? 'text-ag-critical border-ag-critical'
                  : 'text-gray-500 border-[#4A443D]'
              }`}
            >
              Halted: {loading ? '--' : status.halted}
            </Badge>
          </div>
        </div>

        {/* Audit trail persistence status */}
        <div className="flex items-center gap-2">
          <Database className="w-4 h-4 text-[#A8A29E]" />
          {status.auditPersistent ? (
            <span className="text-xs text-ag-active flex items-center gap-1">
              Audit Trail Persistent
            </span>
          ) : (
            <span className="text-xs text-ag-critical flex items-center gap-1">
              Audit Trail Not Persistent
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
